
import { Link } from 'react-router-dom';

export default function ArticlePartOne() {
  return (
    <div className="prose prose-lg max-w-none">
      <p className="text-xl text-gray-700 leading-relaxed mb-8">
        <strong>El Paso closing costs</strong> catch most home sellers off guard. You accept an
        offer, celebrate the sale price, and then discover that thousands of dollars will be
        deducted from your proceeds before you see a dime. Agent commissions, title fees,
        property tax prorations, transfer taxes, repair credits, and a half‑dozen other line
        items quietly eat into the equity you spent years building. If you are planning to sell a
        home in El Paso, Texas, understanding exactly what you will pay at closing is not
        optional—it is the difference between walking away with the money you expected and
        being blindsided by a settlement statement full of surprises. This comprehensive guide
        breaks down every <strong>seller fee</strong> you will encounter in an El Paso real
        estate transaction, shows you real dollar amounts on typical home prices, explains
        which costs are negotiable, and reveals how to keep more of your hard‑earned equity.
      </p>

      <h2
        id="what-are-closing-costs-and-who-pays-them-in-el-paso"
        className="text-4xl font-bold text-black mt-16 mb-6"
      >
        What Are Closing Costs and Who Pays Them in El Paso?
      </h2>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Closing costs are the fees and expenses that both buyers and sellers pay to finalize a
        real estate transaction. They cover everything from agent commissions and title
        insurance to government recording fees and prorated taxes. In Texas, there is no state
        law that dictates which party pays which costs—everything is negotiable. However,
        El Paso has strong local customs that determine the typical split, and deviating from
        those customs can complicate negotiations.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        In a standard El Paso home sale, the <strong>seller typically pays between eight and ten
        percent</strong> of the sale price in total closing costs. The single largest expense is
        the real estate agent commission, which usually accounts for five to six percent of the
        sale price. The remaining costs include the owner's title insurance policy (which the
        seller customarily pays in El Paso), prorated property taxes, any outstanding liens or
        judgments, repair credits negotiated during the inspection period, and various
        administrative fees.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        The buyer also has closing costs—typically two to four percent of the purchase price—covering
        their lender's title policy, loan origination fees, appraisal, survey, and prepaid escrow
        items. In a competitive market, sellers sometimes agree to pay a portion of the buyer's
        closing costs as a concession to close the deal, which further increases the seller's
        total out‑of‑pocket expenses.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Understanding this split matters because it directly affects your <strong>net proceeds</strong>—the
        actual cash you walk away with after every fee is paid. On a home selling for the{' '}
        <Link
          to="/market-data-el-paso-texas"
          className="text-[#D4AF37] hover:underline font-semibold"
        >
          El Paso median price of approximately $274,200
        </Link>
        , total seller closing costs can range from $21,900 to $27,400. That is a significant
        amount of money, and knowing where every dollar goes puts you in a stronger position to
        negotiate and plan.
      </p>

      <div className="rounded-2xl overflow-hidden shadow-lg my-12 h-80">
        <img
          src="https://public.readdy.ai/ai/img_res/ec7b27545fa719a014165e4fc7117a01.jpg"
          alt="Real estate closing meeting at El Paso title company with buyers and sellers signing documents"
          title="El Paso home sale closing process at title company"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <h2
        id="complete-breakdown-of-el-paso-seller-closing-costs"
        className="text-4xl font-bold text-black mt-16 mb-6"
      >
        Complete Breakdown of El Paso Seller Closing Costs
      </h2>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        Here is a detailed breakdown of every cost a typical El Paso home seller can expect to pay
        at closing. We have calculated dollar amounts based on three common sale prices to give
        you a realistic picture of what to budget.
      </p>

      <div className="bg-gray-50 rounded-2xl p-6 md:p-8 my-10 border border-gray-200">
        <h3 className="text-xl font-bold text-black mb-6">El Paso Seller Closing Costs Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="py-3 pr-4 font-bold text-black">Cost Item</th>
                <th className="py-3 pr-4 font-bold text-black text-right">$200K Sale</th>
                <th className="py-3 pr-4 font-bold text-black text-right">$274K Sale</th>
                <th className="py-3 font-bold text-black text-right">$350K Sale</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">Agent Commission (6%)</td>
                <td className="py-3 pr-4 text-right">$12,000</td>
                <td className="py-3 pr-4 text-right">$16,452</td>
                <td className="py-3 text-right">$21,000</td>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <td className="py-3 pr-4 font-medium">Owner&apos;s Title Insurance</td>
                <td className="py-3 pr-4 text-right">$1,295</td>
                <td className="py-3 pr-4 text-right">$1,680</td>
                <td className="py-3 text-right">$2,050</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">Escrow / Closing Fee</td>
                <td className="py-3 pr-4 text-right">$500</td>
                <td className="py-3 pr-4 text-right">$500</td>
                <td className="py-3 text-right">$600</td>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <td className="py-3 pr-4 font-medium">Property Tax Proration</td>
                <td className="py-3 pr-4 text-right">$2,740</td>
                <td className="py-3 pr-4 text-right">$3,756</td>
                <td className="py-3 text-right">$4,795</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">HOA Transfer / Resale Cert</td>
                <td className="py-3 pr-4 text-right">$250</td>
                <td className="py-3 pr-4 text-right">$250</td>
                <td className="py-3 text-right">$350</td>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <td className="py-3 pr-4 font-medium">Home Warranty (buyer credit)</td>
                <td className="py-3 pr-4 text-right">$500</td>
                <td className="py-3 pr-4 text-right">$550</td>
                <td className="py-3 text-right">$600</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">Repair Credits (avg.)</td>
                <td className="py-3 pr-4 text-right">$1,500</td>
                <td className="py-3 pr-4 text-right">$2,500</td>
                <td className="py-3 text-right">$3,500</td>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <td className="py-3 pr-4 font-medium">Recording &amp; Admin Fees</td>
                <td className="py-3 pr-4 text-right">$150</td>
                <td className="py-3 pr-4 text-right">$150</td>
                <td className="py-3 text-right">$200</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-3 pr-4 font-medium">Mortgage Payoff &amp; Release</td>
                <td className="py-3 pr-4 text-right">$150</td>
                <td className="py-3 pr-4 text-right">$150</td>
                <td className="py-3 text-right">$150</td>
              </tr>
              <tr className="bg-black/5 font-bold">
                <td className="py-3 pr-4 text-black">Estimated Total Seller Costs</td>
                <td className="py-3 pr-4 text-right text-black">~$19,085</td>
                <td className="py-3 pr-4 text-right text-black">~$25,988</td>
                <td className="py-3 text-right text-black">~$33,245</td>
              </tr>
              <tr className="bg-[#D4AF37]/10 font-bold">
                <td className="py-3 pr-4 text-black">Percentage of Sale Price</td>
                <td className="py-3 pr-4 text-right text-black">~9.5%</td>
                <td className="py-3 pr-4 text-right text-black">~9.5%</td>
                <td className="py-3 text-right text-black">~9.5%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-gray-500 mt-4">
          *Amounts are estimates based on typical El Paso transactions. Actual costs vary by
          property, negotiation, and specific circumstances. Property tax proration assumes a
          mid‑year closing. HOA fees apply only if the property is in an HOA community. Repair
          credits vary widely based on inspection findings.
        </p>
      </div>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        As the table shows, a seller in El Paso can expect to pay roughly <strong>eight to ten
        percent</strong> of the sale price in total closing costs. On the median‑priced home,
        that is nearly $26,000 coming out of your proceeds. The agent commission alone accounts
        for more than sixty percent of total seller costs, which is why commission rates are
        one of the most impactful factors in your net proceeds calculation.
      </p>

      <p className="text-lg text-gray-700 leading-relaxed mb-6">
        It is worth noting that these figures assume a traditional sale with real estate agents
        on both sides. If you sell to a cash buyer, several of these line items—including the
        entire agent commission, home warranty, and often repair credits—can be eliminated
        entirely, which we will explore in detail later in this guide.
      </p>
    </div>
  );
}
